import * as React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { CustomProvider, DateRangePicker } from 'rsuite';
import 'rsuite/dist/rsuite-no-reset.min.css';
import './style.css';
import type { SolidRange, Props } from './types';
import { useSelector } from 'react-redux';
import { useFetchOpeningBalanceDateQuery } from '../../../../redux/apis/openingbalacedate.api';
import { showWarningSnackbar } from '../../../snackBar/snakbar';

const toStartOfDayDate = (d: number | Date | Dayjs) => dayjs(d).startOf('day').toDate();
const toEndOfDayDate = (d: number | Date | Dayjs) => dayjs(d).endOf('day').toDate();

export const DateRangeRsuite = React.forwardRef<{ openDatePicker: () => void }, Props>(function DateRangeRsuite(
  {
    value = null,
    onChange,
    showOneCalendar = false,
    disabled = false,
    style = {},
    hidePresets = false,
    containerRef,
    className = '',
    shouldDisableDate,
    restrictToFinancialYear = true,
    matches,
    onValidationSuccess,
    onOk,
    ...rsuiteProps
  }: Props,
  ref
) {
  // Internal state to track what user is typing (not emitted until confirmed)
  const { data: openingBalanceDate, isLoading } = useFetchOpeningBalanceDateQuery(
    {
      invoicetypecd: 'Sales',
      invoiceid: 0,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );
  const [internalValue, setInternalValue] = React.useState<SolidRange | null>(null);
  const internalContainerRef = React.useRef<HTMLSpanElement>(null);
  const financialYear: any = useSelector((state: any) => state?.userPrefrenceandReducer?.financialYear);
  const rawFyStart: number | null = typeof financialYear?.startdate === 'number' ? financialYear.startdate : null;
  const rawFyEnd: number | null = typeof financialYear?.enddate === 'number' ? financialYear.enddate : null;
  const preventDate = dayjs(openingBalanceDate?.getMinMaxInvoiceDate?.openingbalancedate).toDate();
  const fyStart =
    restrictToFinancialYear && preventDate
      ? new Date(preventDate).getTime() / 1000
      : typeof rawFyStart === 'number'
      ? rawFyStart > 1e12
        ? Math.floor(rawFyStart / 1000)
        : rawFyStart
      : null;
  const fyEnd = typeof rawFyEnd === 'number' ? (rawFyEnd > 1e12 ? Math.floor(rawFyEnd / 1000) : rawFyEnd) : null;
  console.log(fyStart, fyEnd, 'fyEnd');

  const DisableWithInFinancialYear = (date: Date) => {
    if (!fyStart || !fyEnd) return false;
    const dateTime = date.getTime() / 1000;
    // if (restrictToFinancialYear && preventDate) {
    //   // Convert preventDate to timestamp if it's a string, or use as-is if it's already a number
    //   const preventDateTime = new Date(preventDate).getTime() / 1000;

    //   return dateTime < preventDateTime || dateTime > fyEnd;
    // }
    return dateTime < fyStart || dateTime > fyEnd;
  };

  const today = React.useRef(dayjs()).current;
  const [rsuiteTheme] = React.useState<'dark' | 'light'>('light');
  const [open, setOpen] = React.useState(false);
  const [preventClose, setPreventClose] = React.useState(false);
  const [hasUserInteracted, setHasUserInteracted] = React.useState(false);

  React.useImperativeHandle(
    ref,
    () => ({
      openDatePicker: () => setOpen(true),
    }),
    []
  );

  React.useEffect(() => {
    const el = containerRef?.current;
    if (!el) return;

    if (open) {
      const prevOverflow = el.style.overflow;
      el.dataset.prevOverflow = prevOverflow;
      el.style.overflow = 'hidden';
      return () => {
        el.style.overflow = el.dataset.prevOverflow || '';
        delete el.dataset.prevOverflow;
      };
    } else {
      el.style.overflow = el.dataset.prevOverflow || '';
      delete el.dataset.prevOverflow;
    }
  }, [open, containerRef]);

  const presets = React.useMemo(
    (): { label: string; value: SolidRange }[] => [
      { label: 'Today', value: [toStartOfDayDate(today), toEndOfDayDate(today)] },
      { label: 'Last 7 days', value: [toStartOfDayDate(today.subtract(6, 'day')), toEndOfDayDate(today)] },
      { label: 'This Month', value: [toStartOfDayDate(today.startOf('month')), toEndOfDayDate(today.endOf('month'))] },
    ],
    [today]
  );

  // Calculate default calendar value based on financial year
  const defaultCalendarValue = React.useMemo((): [Date, Date] => {
    if (!fyStart || !fyEnd) {
      // No financial year restrictions, show today
      const todayDate = today.toDate();
      return [todayDate, todayDate];
    }

    const todayUnix = Math.floor(today.valueOf() / 1000);

    // Check if today is within the financial year
    if (todayUnix >= fyStart && todayUnix <= fyEnd) {
      // Today is within FY, show today
      const todayDate = today.toDate();
      return [todayDate, todayDate];
    } else {
      // Today is outside FY, show the start of the financial year
      const fyStartDate = dayjs.unix(fyStart).toDate();
      return [fyStartDate, fyStartDate];
    }
  }, [today, fyStart, fyEnd]);

  // Sync internal value with external value when it changes
  React.useEffect(() => {
    const externalValue: SolidRange | null =
      value && value[0] != null && value[1] != null ? [dayjs.unix(value[0]).toDate(), dayjs.unix(value[1]).toDate()] : null;
    setInternalValue(externalValue);
  }, [value]);

  // Function to emit the confirmed value
  const emitConfirmedValue = React.useCallback(
    (confirmedValue: SolidRange | null) => {
      const out = {
        from: confirmedValue ? dayjs(confirmedValue[0]).startOf('day').unix() : null,
        to: confirmedValue ? dayjs(confirmedValue[1]).endOf('day').unix() : null,
      };
      onChange?.(out);
    },
    [onChange]
  );

  const isPresetSelectionEvent = React.useCallback((event: unknown) => {
    const target = (event as { target?: EventTarget | null })?.target as HTMLElement | null;
    if (!target) return false;
    return Boolean(target.closest('.rs-picker-toolbar-ranges'));
  }, []);

  // Handle clear action - emit onChange when user clears dates
  const handleClean = React.useCallback(() => {
    setInternalValue(null);
    setHasUserInteracted(true); // Mark that user has interacted
    // Emit null values immediately when user clears
    emitConfirmedValue(null);
  }, [emitConfirmedValue]);

  // Validation function with detailed error messages
  const validateDateRange = React.useCallback(
    (value: SolidRange | null): { isValid: boolean; errorMessage?: string } => {
      // If value is null, check if user has interacted
      if (!value) {
        // If user hasn't interacted, they need to select dates first
        if (!hasUserInteracted) {
          return { isValid: false, errorMessage: 'Please select a date range first' };
        }
        // If user has interacted and cleared dates, null is valid (cleared state)
        return { isValid: true };
      }
      
      if (!Array.isArray(value) || value.length !== 2) {
        return { isValid: false, errorMessage: 'Please select both start and end dates' };
      }

      const [startDate, endDate] = value;

      // Check if both dates are valid Date objects
      if (!startDate || !endDate || !(startDate instanceof Date) || !(endDate instanceof Date)) {
        return { isValid: false, errorMessage: 'Please select both start and end dates' };
      }

      // Check if dates are valid (not NaN)
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return { isValid: false, errorMessage: 'Please select valid dates' };
      }

      // Check if start date is before end date
      if (startDate > endDate) {
        return { isValid: false, errorMessage: 'Start date must be before end date' };
      }

      // Check financial year restrictions if enabled
      if (restrictToFinancialYear && fyStart && fyEnd) {
        const startUnix = Math.floor(startDate.getTime() / 1000);
        const endUnix = Math.floor(endDate.getTime() / 1000);

        if (startUnix < fyStart || endUnix > fyEnd) {
          const fyStartFormatted = dayjs.unix(fyStart).format('DD/MM/YYYY');
          const fyEndFormatted = dayjs.unix(fyEnd).format('DD/MM/YYYY');
          return { 
            isValid: false, 
            errorMessage: `Date range must be within financial year (${fyStartFormatted} to ${fyEndFormatted})` 
          };
        }
      }

      return { isValid: true };
    },
    [restrictToFinancialYear, fyStart, fyEnd, hasUserInteracted]
  );

  // Handle internal changes; preset selections should commit immediately.
  const handleInternalChange = React.useCallback((next: SolidRange | null, event?: unknown) => {
    setInternalValue(next);
    setHasUserInteracted(true); // Mark that user has interacted

    const hasCompleteRange = Array.isArray(next) && next[0] instanceof Date && next[1] instanceof Date;
    if (!hasCompleteRange || !isPresetSelectionEvent(event)) return;

    const validation = validateDateRange(next);
    if (!validation.isValid) {
      showWarningSnackbar(validation.errorMessage || 'Invalid date range');
      return;
    }

    emitConfirmedValue(next);
    onValidationSuccess?.();
    setOpen(false);
  }, [emitConfirmedValue, isPresetSelectionEvent, onValidationSuccess, validateDateRange]);

  // Custom close handler that respects validation state
  const handleClose = React.useCallback(() => {
    if (preventClose) {
      // Don't close if validation failed
      setPreventClose(false);
      return;
    }
    setOpen(false);
  }, [preventClose]);


  // Custom OK handler that prevents closing on validation failure
  const handleOk = React.useCallback((value: SolidRange | null,event:React.SyntheticEvent) => {
    // Validate the value passed directly from onOk event
    const validation = validateDateRange(value);
    if (validation.isValid) {
      // Update internal state with the validated value
      setInternalValue(value);
      emitConfirmedValue(value);
      onValidationSuccess?.(); // Call the success callback to close popup
      setOpen(false);
      onOk?.(value,event); // Call external onOk if provided
      return true; // Success
    } else {
      // Show warning message and keep date picker open
      showWarningSnackbar(validation.errorMessage || 'Invalid date range');
      setPreventClose(true);
      return false; // Validation failed
    }
  }, [validateDateRange, emitConfirmedValue, onValidationSuccess]);

  // Add event listeners for Enter key using ref
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        
        // Validate current internal value
        const validation = validateDateRange(internalValue);
        if (validation.isValid) {
          emitConfirmedValue(internalValue);
          onValidationSuccess?.();
          setOpen(false);
        } else {
          showWarningSnackbar(validation.errorMessage || 'Invalid date range');
          // Keep focus on the current input
          const target = event.target as HTMLInputElement;
          if (target && typeof target.focus === 'function') {
            setTimeout(() => {
              target.focus();
            }, 0);
          }
        }
      }

      if (event.key === 'Tab') {
        // Check validation before allowing tab navigation
        const validation = validateDateRange(internalValue);
        if (!validation.isValid) {
          event.preventDefault();
          event.stopPropagation();
          showWarningSnackbar(validation.errorMessage || 'Invalid date range');
          const target = event.target as HTMLInputElement;
          if (target && typeof target.focus === 'function') {
            setTimeout(() => {
              target.focus();
            }, 0);
          }
        }
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    if (internalContainerRef.current) {
      const container = internalContainerRef.current;
      const inputs = container.querySelectorAll('input');

      // Add listeners to both container and individual inputs
      container.addEventListener('keydown', handleKeyDown, true);
      container.addEventListener('keyup', handleKeyUp, true);

      inputs.forEach((input) => {
        // Use capture phase to intercept events before other handlers
        input.addEventListener('keydown', handleKeyDown, true);
        input.addEventListener('keyup', handleKeyUp, true);
      });

      return () => {
        container.removeEventListener('keydown', handleKeyDown, true);
        container.removeEventListener('keyup', handleKeyUp, true);
        inputs.forEach((input) => {
          input.removeEventListener('keydown', handleKeyDown, true);
          input.removeEventListener('keyup', handleKeyUp, true);
        });
      };
    }
  }, [validateDateRange, internalValue, emitConfirmedValue, onValidationSuccess]);
  return (
    <span className="rs-stack" ref={internalContainerRef}>
      <CustomProvider theme={rsuiteTheme}>
        <DateRangePicker
          value={internalValue}
          onChange={handleInternalChange}
          style={{ minWidth: '250px', ...style }}
          className={rsuiteTheme + ' ' + className}
          menuClassName={'mui-like-rsuite ' + rsuiteTheme}
          format="dd/MM/yyyy"
          shouldDisableDate={restrictToFinancialYear ? DisableWithInFinancialYear : shouldDisableDate}
          ranges={hidePresets ? [] : presets}
          showOneCalendar={showOneCalendar}
          placeholder="DD/MM/YYYY to DD/MM/YYYY"
          placement="bottomStart"
          preventOverflow
          cleanable
          disabled={disabled}
          open={open}
          onOpen={() => {
            setOpen(true);
            setPreventClose(false); // Reset prevent close state when opening
            setHasUserInteracted(false); // Reset interaction state
          }}
          onClose={handleClose}
          onOk={handleOk}
          onClean={handleClean}
          defaultCalendarValue={defaultCalendarValue}
          {...rsuiteProps}
        />
      </CustomProvider>
    </span>
  );
});
